import { RegionContext } from 'components/discover/contexts/RegionContext';
import { SeeMorePublicationsMonoCategory } from 'components/discover/publications/see-more/see-more-publications-mono-category';
import { SeeMorePublicationsMultiCategories } from 'components/discover/publications/see-more/see-more-publications-multi-categories';
import { VideoBrowser } from 'components/discover/publications/video-browser/video-browser';
import { useFetchData } from 'hooks/useFetchData';
import type { IntlKey } from 'locales';
import { type ReactNode, useCallback, useContext, useMemo } from 'react';
import { LoadingSkeletonTheme } from 'routes/LoadingSkeletonTheme';
import type { Observable } from 'rxjs';
import type { SgBsTheme } from 'state/userPreferences/userPreferences.model';

import type { SeeMorePublicationResult } from './model';
import { SeeMoreTechnicalError } from './see-more-technical-error';

interface SeeMorePublicationsContainerProps {
  titleKey: IntlKey;
  allKey: IntlKey;
  theme: SgBsTheme;
  onClose: () => void;
  api: (region: string) => Observable<Readonly<SeeMorePublicationResult>>;
  skeleton: ReactNode;
  modalContentType: 'videos' | 'products' | 'thematics';
}

const emptyArray: readonly [] = [];

export function SeeMorePublicationsContainer({
  onClose,
  api,
  titleKey,
  skeleton,
  theme,
  modalContentType,
  allKey,
}: SeeMorePublicationsContainerProps) {
  const { region } = useContext(RegionContext);

  const fetchMorePublications = useCallback(() => api(region), [api, region]);

  const [status, data, retry] = useFetchData<SeeMorePublicationResult>(fetchMorePublications, d => {
    return d.categories.length === 0 || d.items.length === 0;
  });

  const items = data?.items;
  const categories = data?.categories;
  const keywords = data?.keywords;

  const nonEmptyCategories = useMemo(() => {
    if (status !== 'success' || items === undefined || categories === undefined) {
      return emptyArray;
    }
    return categories.filter(c => items.some(i => i.categoryId === c.id));
  }, [categories, items, status]);

  switch (status) {
    case 'success':
      if (modalContentType === 'videos') {
        return <VideoBrowser items={items!} categories={nonEmptyCategories} keywords={keywords!} onClose={onClose} />;
      }

      if (nonEmptyCategories.length <= 1) {
        return <SeeMorePublicationsMonoCategory items={items!} titleKey={titleKey} onClose={onClose} />;
      }

      return (
        <SeeMorePublicationsMultiCategories
          onClose={onClose}
          items={items!}
          categories={nonEmptyCategories}
          titleKey={titleKey}
          allKey={allKey}
        />
      );
    case 'loading':
      return <LoadingSkeletonTheme theme={theme}>{skeleton}</LoadingSkeletonTheme>;
    default:
      return <SeeMoreTechnicalError retry={retry!} />;
  }
}

SeeMorePublicationsContainer.displayName = 'SeeMorePublicationsContainer';
